import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import { decryptData } from "../../Core/util/obfuscate";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Divider,
  Paper,
  Grid,
  useTheme,
  Avatar,
  FormControlLabel,
  Chip,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PhoneIcon from "@mui/icons-material/Phone";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SecurityIcon from "@mui/icons-material/Security";
import BusinessIcon from "@mui/icons-material/Business";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import { toast } from "react-toastify";

const InfoItem = ({ label, value, icon }: { label: string; value?: string; icon: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "16px",
      p: "16px",
      borderRadius: "12px",
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: "15px" }}>
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

export default function AdminDetail({ data, updatedetails, readOnly }: any) {
  const theme = useTheme();
  const [adminData, setAdminData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Fields for updating
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const defaultPermission = {
    users: {
      read: false,
      write: false,
      userDetails: { read: false, write: false },
      companyDetails: { read: false },
      subscriptionDetail: { read: false, write: false },
      deleteRequests: { read: false, write: false }
    },
    companies: {
      read: false,
      write: false,
      deleteCompany: { write: false }
    },
    socketConnections: {
      read: false,
      write: false
    },
    logs: {
      read: false
    },
    subscriptions: {
      read: false,
      write: false
    },
    diagnostic: {
      read: false,
      write: false
    },
    tickets: {
      read: false,
      write: false
    },
    partners: {
      read: false,
      write: false
    },
    enquiries: {
      read: false,
      write: false
    },
    adminSettings: {
      read: false,
      write: false,
      appConfig: { read: false, write: false },
      manageAdmins: { read: false, write: false }
    }
  };

  const [permissions, setPermissions] = useState<any>(defaultPermission);

  const handlePermissionChange = (path: string[], val: boolean) => {
    setPermissions((prev: any) => {
      const updated = JSON.parse(JSON.stringify(prev));
      let current = updated;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) current[path[i]] = {};
        current = current[path[i]];
      }
      current[path[path.length - 1]] = val;
      return updated;
    });
  };

  const setAllInSection = (section: string, value: boolean) => {
    setPermissions((prev: any) => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (section === "users") {
        updated.users.read = value;
        updated.users.write = value;
        updated.users.userDetails.read = value;
        updated.users.userDetails.write = value;
        updated.users.companyDetails.read = value;
        updated.users.subscriptionDetail.read = value;
        updated.users.subscriptionDetail.write = value;
        updated.users.deleteRequests.read = value;
        updated.users.deleteRequests.write = value;
      } else if (section === "companies") {
        updated.companies.read = value;
        updated.companies.write = value;
        updated.companies.deleteCompany.write = value;
        updated.socketConnections.read = value;
        updated.socketConnections.write = value;
        updated.logs.read = value;
      } else if (section === "admin") {
        updated.subscriptions.read = value;
        updated.subscriptions.write = value;
        updated.diagnostic.read = value;
        updated.diagnostic.write = value;
        updated.tickets.read = value;
        updated.tickets.write = value;
      } else if (section === "settings") {
        updated.partners.read = value;
        updated.partners.write = value;
        updated.enquiries.read = value;
        updated.enquiries.write = value;
        updated.adminSettings.read = value;
        updated.adminSettings.appConfig.read = value;
        updated.adminSettings.appConfig.write = value;
        updated.adminSettings.manageAdmins.read = value;
        updated.adminSettings.manageAdmins.write = value;
      }
      return updated;
    });
  };

  const [updateModal, setUpdateModal] = useState(false);

  const handleOpenUpdateModal = () => {
    setName(adminData?.name || adminData?.userName || "");
    setPhone(adminData?.phone || "");
    setIsSuperAdmin(adminData?.isSuperAdmin || adminData?.superAdmin || false);
    
    const existingPerms = adminData?.permission || adminData?.permissions || {};
    setPermissions({
      ...defaultPermission,
      ...existingPerms
    });
    setUpdateModal(true);
  };

  const handleCloseUpdateModal = () => setUpdateModal(false);

  const handleGetadminData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${ipconfig}/adminpanel/fetchbyid?adminId=${data?._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        const fetched = response?.data?.data || {};
        setAdminData(fetched);
        setIsSuperAdmin(fetched?.isSuperAdmin || fetched?.superAdmin || false);
        const existingPerms = fetched?.permission || fetched?.permissions || {};
        setPermissions({
          ...defaultPermission,
          ...existingPerms
        });
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error, "Error fetching admin details");
      setIsLoading(false);
    }
  };

  const updateUserData = async () => {
    try {
      const obj = {
        name,
        phone,
        isSuperAdmin,
        permissions: isSuperAdmin ? {} : permissions,
      };
      const response = await axios.put(
        `${ipconfig}/adminpanel/update?adminId=${data?._id}`,
        obj,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Admin updated successfully!");
        const cachedMe = localStorage.getItem("adminMe");
        if (cachedMe) {
          try {
            const parsed = decryptData(cachedMe);
            if (parsed && parsed._id === data?._id) {
              localStorage.removeItem("adminMe");
              window.location.reload();
            }
          } catch (e) {
            localStorage.removeItem("adminMe");
          }
        }
        handleCloseUpdateModal();
        handleGetadminData();
        if (updatedetails) updatedetails();
      }
    } catch (error) {
      console.log(error, "Error updating admin data");
      toast.error("Failed to update admin data.");
    }
  };

  useEffect(() => {
    if (data?._id) {
      handleGetadminData();
    }
  }, [data]);

  if (isLoading || (!adminData?.name && !adminData?.userName)) {
    return <Spinner height="100%" />;
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Header Panel */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: "48px", height: "48px" }}>
            <AdminPanelSettingsIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
              {adminData.name || adminData.userName}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Role: {adminData.role || "Administrator"}
            </Typography>
          </Box>
        </Box>

        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleOpenUpdateModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Edit Admin
          </Button>
        )}
      </Box>

      <Divider />

      {/* Admin general details */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={3}>
          <InfoItem label="Admin Name" value={adminData.name || adminData.userName} icon={<AccountCircleIcon />} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InfoItem label="Email Address" value={adminData.email} icon={<AccountCircleIcon />} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InfoItem label="Phone Number" value={adminData.phone} icon={<PhoneIcon />} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InfoItem label="Created By" value={adminData.createdBy} icon={<AdminPanelSettingsIcon />} />
        </Grid>
      </Grid>

      <Divider />

      {/* Permissions Grid */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "text.primary" }}>
          Permission Groups
        </Typography>

        {isSuperAdmin ? (
          <Paper variant="outlined" sx={{ p: "24px", borderRadius: "12px", border: "1px solid", borderColor: "primary.main", backgroundColor: "action.hover" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 750, color: "primary.main", mb: 1, display: "flex", alignItems: "center", gap: "8px" }}>
              <SecurityIcon />
              Super Administrator
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              This user holds full, unrestricted system access. All permissions are implicitly granted.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {/* Users Directory Card */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: "20px", borderRadius: "12px", height: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "primary.main", mb: 2, display: "flex", alignItems: "center", gap: "8px" }}>
                  <AccountCircleIcon sx={{ fontSize: 20 }} />
                  Users Directory
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    { label: "Read User List", checked: permissions.users?.read },
                    { label: "Write / Edit Users", checked: permissions.users?.write },
                    { label: "Read User Details Tab", checked: permissions.users?.userDetails?.read },
                    { label: "Write User Details Tab", checked: permissions.users?.userDetails?.write },
                    { label: "Read Linked Companies", checked: permissions.users?.companyDetails?.read },
                    { label: "Read Subscriptions Detail", checked: permissions.users?.subscriptionDetail?.read },
                    { label: "Write Subscriptions Detail", checked: permissions.users?.subscriptionDetail?.write },
                    { label: "Read Account Delete Requests", checked: permissions.users?.deleteRequests?.read },
                    { label: "Approve Account Deletions", checked: permissions.users?.deleteRequests?.write },
                  ].map((perm, idx) => (
                    <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.8, borderBottom: "1px solid", borderColor: "divider" }}>
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {perm.label}
                      </Typography>
                      <Chip
                        label={perm.checked ? "Allowed" : "Denied"}
                        size="small"
                        color={perm.checked ? "success" : "default"}
                        sx={{ fontWeight: 700, fontSize: "10px", height: "20px" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Companies & Connections Card */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: "20px", borderRadius: "12px", height: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "primary.main", mb: 2, display: "flex", alignItems: "center", gap: "8px" }}>
                  <BusinessIcon sx={{ fontSize: 20 }} />
                  Companies & Connections
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    { label: "Read Companies List", checked: permissions.companies?.read },
                    { label: "Write / Sync Companies", checked: permissions.companies?.write },
                    { label: "Delete Company Nodes", checked: permissions.companies?.deleteCompany?.write },
                    { label: "Read Live Sockets", checked: permissions.socketConnections?.read },
                    { label: "Disconnect Device Sockets", checked: permissions.socketConnections?.write },
                    { label: "Read System Logs Console", checked: permissions.logs?.read },
                  ].map((perm, idx) => (
                    <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.8, borderBottom: "1px solid", borderColor: "divider" }}>
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {perm.label}
                      </Typography>
                      <Chip
                        label={perm.checked ? "Allowed" : "Denied"}
                        size="small"
                        color={perm.checked ? "success" : "default"}
                        sx={{ fontWeight: 700, fontSize: "10px", height: "20px" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Subscriptions Plans, Diagnostics, Tickets, Partners */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: "20px", borderRadius: "12px", height: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "primary.main", mb: 2, display: "flex", alignItems: "center", gap: "8px" }}>
                  <SecurityIcon sx={{ fontSize: 20 }} />
                  Administrative Modules
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    { label: "Read Subscriptions / Plans", checked: permissions.subscriptions?.read },
                    { label: "Write / Create Plans", checked: permissions.subscriptions?.write },
                    { label: "Read Diagnostics", checked: permissions.diagnostic?.read },
                    { label: "Execute Diagnostics", checked: permissions.diagnostic?.write },
                    { label: "Read Support Tickets", checked: permissions.tickets?.read },
                    { label: "Write / Reply to Tickets", checked: permissions.tickets?.write },
                  ].map((perm, idx) => (
                    <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.8, borderBottom: "1px solid", borderColor: "divider" }}>
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {perm.label}
                      </Typography>
                      <Chip
                        label={perm.checked ? "Allowed" : "Denied"}
                        size="small"
                        color={perm.checked ? "success" : "default"}
                        sx={{ fontWeight: 700, fontSize: "10px", height: "20px" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Settings, Partners, Enquiries */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: "20px", borderRadius: "12px", height: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "primary.main", mb: 2, display: "flex", alignItems: "center", gap: "8px" }}>
                  <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />
                  Settings , Partners & Admin management
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    { label: "Read Partner Referrals", checked: permissions.partners?.read },
                    { label: "Write Referral payouts / plans", checked: permissions.partners?.write },
                    { label: "Read Inbound Enquiries", checked: permissions.enquiries?.read },
                    { label: "Update Enquiry Status", checked: permissions.enquiries?.write },
                    { label: "Read App Settings", checked: permissions.adminSettings?.read },
                    { label: "Read App Config Toggles", checked: permissions.adminSettings?.appConfig?.read },
                    { label: "Toggle App Toggles", checked: permissions.adminSettings?.appConfig?.write },
                    { label: "Read Sub-Admins Directory", checked: permissions.adminSettings?.manageAdmins?.read },
                    { label: "Manage Admin Users & permissions", checked: permissions.adminSettings?.manageAdmins?.write },
                  ].map((perm, idx) => (
                    <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.8, borderBottom: "1px solid", borderColor: "divider" }}>
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {perm.label}
                      </Typography>
                      <Chip
                        label={perm.checked ? "Allowed" : "Denied"}
                        size="small"
                        color={perm.checked ? "success" : "default"}
                        sx={{ fontWeight: 700, fontSize: "10px", height: "20px" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Edit Admin Dialog */}
      <Dialog
        open={updateModal}
        onClose={handleCloseUpdateModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit Admin User & Permissions
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "16px", p: "24px", maxHeight: "70vh", overflowY: "auto" }}>
          <TextField
            label="Name"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Phone"
            fullWidth
            size="small"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <Divider sx={{ my: 1 }} />

          <FormControlLabel
            control={
              <Switch
                checked={isSuperAdmin}
                onChange={(e) => setIsSuperAdmin(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                Super Admin (Grants access to all sections)
              </Typography>
            }
          />

          {!isSuperAdmin && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "primary.main", mb: 1 }}>
                Configure Custom Permissions
              </Typography>

              <Grid container spacing={3}>
                {/* Users permissions */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Users Management
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("users", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("users", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.read} onChange={(e) => handlePermissionChange(["users", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Directory</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.write} onChange={(e) => handlePermissionChange(["users", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Edit Users</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.userDetails.read} onChange={(e) => handlePermissionChange(["users", "userDetails", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Details Tab</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.userDetails.write} onChange={(e) => handlePermissionChange(["users", "userDetails", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write User Details Tab</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.companyDetails.read} onChange={(e) => handlePermissionChange(["users", "companyDetails", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Linked Companies</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.subscriptionDetail.read} onChange={(e) => handlePermissionChange(["users", "subscriptionDetail", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Subscription Details</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.subscriptionDetail.write} onChange={(e) => handlePermissionChange(["users", "subscriptionDetail", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write User Subscription Details</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.deleteRequests.read} onChange={(e) => handlePermissionChange(["users", "deleteRequests", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Account Deletion Requests</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.users.deleteRequests.write} onChange={(e) => handlePermissionChange(["users", "deleteRequests", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Approve Account Deletions</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Companies permissions */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Companies & Connections
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("companies", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("companies", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.companies.read} onChange={(e) => handlePermissionChange(["companies", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Companies Directory</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.companies.write} onChange={(e) => handlePermissionChange(["companies", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Sync Companies</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.companies.deleteCompany.write} onChange={(e) => handlePermissionChange(["companies", "deleteCompany", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Delete Company Node</Typography>}
                      />
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                        Sockets & Diagnostic Logs
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.socketConnections.read} onChange={(e) => handlePermissionChange(["socketConnections", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Live Sockets</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.socketConnections.write} onChange={(e) => handlePermissionChange(["socketConnections", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Disconnect Device Sockets</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.logs.read} onChange={(e) => handlePermissionChange(["logs", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read System Logs Console</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Subscriptions Plans, Diagnostics, Tickets, Partners */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Administrative Modules
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("admin", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("admin", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.subscriptions.read} onChange={(e) => handlePermissionChange(["subscriptions", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Global Subscriptions / Plans</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.subscriptions.write} onChange={(e) => handlePermissionChange(["subscriptions", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Create Subscription Plans</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.diagnostic.read} onChange={(e) => handlePermissionChange(["diagnostic", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Diagnostics Dashboard</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.diagnostic.write} onChange={(e) => handlePermissionChange(["diagnostic", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Execute Server Diagnostics</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.tickets.read} onChange={(e) => handlePermissionChange(["tickets", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Support Tickets</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.tickets.write} onChange={(e) => handlePermissionChange(["tickets", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Reply to Tickets</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Partners, Enquiries, Admin Settings */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Partner & System Settings
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("settings", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("settings", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.partners.read} onChange={(e) => handlePermissionChange(["partners", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Partner Referrals</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.partners.write} onChange={(e) => handlePermissionChange(["partners", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write Referral payouts / plans</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.enquiries.read} onChange={(e) => handlePermissionChange(["enquiries", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Inbound Enquiries</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.enquiries.write} onChange={(e) => handlePermissionChange(["enquiries", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Update Enquiry status</Typography>}
                      />
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                        App Configuration & Roles
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.adminSettings.read} onChange={(e) => handlePermissionChange(["adminSettings", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read System Settings</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.adminSettings.appConfig.read} onChange={(e) => handlePermissionChange(["adminSettings", "appConfig", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Global System Toggles</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.adminSettings.appConfig.write} onChange={(e) => handlePermissionChange(["adminSettings", "appConfig", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Toggle Global App Switches</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.adminSettings.manageAdmins.read} onChange={(e) => handlePermissionChange(["adminSettings", "manageAdmins", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Sub-Admins Directory</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permissions.adminSettings.manageAdmins.write} onChange={(e) => handlePermissionChange(["adminSettings", "manageAdmins", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Manage Admin User & permissions</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={handleCloseUpdateModal} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={updateUserData} variant="contained" sx={{ textTransform: "none", fontWeight: 600, px: 3 }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
